<template>
    <div v-if="isSupported() && hasPasskey">
        <ActionButton
            @clicked="handlePasskeyLogin"
            label="🔑 Sign in with Passkey"
            :loading="loading"
            classes="bg-slate-800 hover:bg-slate-600 text-sm px-4 py-2.5 rounded shadow transition-colors text-white font-medium w-full"
        />
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { usePasskey } from '@/composables/usePasskey';
import { router } from '@inertiajs/vue3';
import { notify } from 'notiwind';
import ActionButton from '@/components/action-button.vue';

const props = defineProps({
    userHasPasskeys: {
        type: Boolean,
        default: null, // null means check localStorage
    },
});

const { isSupported, authenticate, loading, error } = usePasskey();

// Use prop if provided, otherwise fall back to localStorage (which is also where
// has_passkey is written below — keep read/write on the same store).
const hasPasskey = computed(() => {
    if (props.userHasPasskeys !== null) {
        return props.userHasPasskeys;
    }
    return localStorage.getItem('has_passkey') === 'true';
});

const handlePasskeyLogin = async () => {
    try {
        const result = await authenticate();
        localStorage.setItem('has_passkey', 'true');
        // Passkey login establishes the Sanctum session server-side; the bearer
        // token is no longer stored in localStorage (SUPPORT-663 / F-024).
        localStorage.setItem('uuid', result.uuid);

        const redirectUrl = result?.redirect || '/dashboard';

        // Delay to ensure session cookie is fully processed by browser before redirect
        // This is critical - the browser needs time to receive and store the session cookie
        // from the authentication response before we navigate to an authenticated route
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Force a full page reload to ensure session is properly established
        // Using location.replace to avoid adding to browser history
        window.location.href = redirectUrl;
    } catch (err) {
        // Remove hint if authentication fails
        localStorage.removeItem('has_passkey');

        // Log the full error so a user can paste from DevTools even if the
        // notification toast is suppressed somewhere up the stack.
        console.error('Passkey login failed', {
            message: err?.message,
            response: err?.response?.data,
            status: err?.response?.status,
        });

        // Prefer the server's actual reason ("Authentication session expired",
        // "Passkey authentication failed", etc.) so a support agent can ask
        // the user for the exact text they saw.
        const serverMessage = err?.response?.data?.message;

        notify(
            {
                group: 'error',
                title: 'Authentication Failed',
                text:
                    serverMessage ||
                    error.value ||
                    'Failed to sign in with passkey. Please try again or use your password.',
            },
            4000
        );
    }
};
</script>
